import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
import EmojiPicker from 'emoji-picker-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Image, Video, FileText, Phone, ExternalLink, MessageSquare,
  Plus, Trash2, Copy, X, CheckCircle, AlertCircle, Info, Bold,
  Italic, Strikethrough, Code2, Hash, Smile, Upload, Shield
} from 'lucide-react';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

// ─────────────────────────────────────────────
//  Render WhatsApp formatting markers visually
// ─────────────────────────────────────────────
const renderWhatsAppText = (text) => {
  if (!text) return null;

  // Split by newlines first to preserve them
  const lines = text.split('\n');
  return lines.map((line, li) => {
    // Process inline formatting: bold (*), italic (_), strikethrough (~), monospace (```)
    // We'll tokenise the line
    const tokens = tokenizeLine(line);
    return (
      <React.Fragment key={li}>
        {tokens.map((tok, ti) => {
          if (tok.type === 'bold') return <strong key={ti}>{tok.content}</strong>;
          if (tok.type === 'italic') return <em key={ti}>{tok.content}</em>;
          if (tok.type === 'strike') return <s key={ti}>{tok.content}</s>;
          if (tok.type === 'mono') return <code key={ti} className="font-mono bg-gray-100 px-0.5 rounded text-[12px]">{tok.content}</code>;
          return <span key={ti}>{tok.content}</span>;
        })}
        {li < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

function tokenizeLine(line) {
  // Patterns: ```...``` (monospace first), *...* (bold), _..._ (italic), ~...~ (strike)
  const regex = /```([\s\S]*?)```|\*(.*?)\*|_(.*?)_|~(.*?)~/g;
  const tokens = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', content: line.slice(lastIndex, match.index) });
    }
    if (match[1] !== undefined) tokens.push({ type: 'mono', content: match[1] });
    else if (match[2] !== undefined) tokens.push({ type: 'bold', content: match[2] });
    else if (match[3] !== undefined) tokens.push({ type: 'italic', content: match[3] });
    else if (match[4] !== undefined) tokens.push({ type: 'strike', content: match[4] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < line.length) {
    tokens.push({ type: 'text', content: line.slice(lastIndex) });
  }
  return tokens.length ? tokens : [{ type: 'text', content: line }];
}

// ─────────────────────────────────────────────
//  WhatsApp Phone Preview
// ─────────────────────────────────────────────
const WhatsAppPreview = ({ template, sampleValues, headerPreview, videoBlob }) => {
  const replaceVariables = (text, values) => {
    if (!text) return text;
    let result = text;
    Object.keys(values).forEach((key) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, values[key] || `{{${key}}}`);
    });
    return result;
  };

  const headerText = replaceVariables(template.header_content, sampleValues);
  const bodyText = replaceVariables(template.body_text, sampleValues);

  return (
    <div className="flex justify-center">
      {/* Phone shell */}
      <div className="relative bg-[#1C1C1E] rounded-[44px] p-[10px] shadow-2xl" style={{ width: 320 }}>
        {/* Top bar: camera + speaker */}
        <div className="flex items-center justify-center h-7 mb-1">
          <div className="w-24 h-[18px] bg-[#0d0d0d] rounded-full flex items-center justify-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#2a2a2a]" />
            <div className="w-[6px] h-[6px] rounded-full bg-[#1f3a5f] opacity-70" />
          </div>
        </div>

        {/* Screen */}
        <div className="rounded-[32px] overflow-hidden flex flex-col" style={{ minHeight: 460 }}>
          {/* WhatsApp top bar */}
          <div className="bg-[#075E54] px-3 py-2 flex items-center gap-2 flex-shrink-0">
            <div className="w-7 h-7 bg-[#128C7E] rounded-full flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[12px] font-semibold leading-none">WhatsApp Business</p>
              <p className="text-[10px] text-green-200 mt-0.5">online</p>
            </div>
          </div>

          {/* Chat area */}
          <div className="flex-1 flex flex-col justify-end p-3"
            style={{ background: "#E4DDD6", backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c5b9ae' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}>
            {/* Message bubble */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden" style={{ maxWidth: 260 }}>
              {/* Header */}
              {template.header_type && (
                <div className="border-b border-gray-100">
                  {template.header_type === 'IMAGE' && (
                    <div className="h-36 bg-gray-200 flex items-center justify-center overflow-hidden">
                      {headerPreview ? (
                        <img src={headerPreview} alt="Header" className="w-full h-full object-cover" />
                      ) : template.header_content?.startsWith('https://') ? (
                        <img src={template.header_content} alt="Header" className="w-full h-full object-cover" />
                      ) : template.header_content ? (
                        <div className="flex flex-col items-center text-gray-500 gap-1">
                          <Image className="w-10 h-10" />
                          <span className="text-[10px] font-medium">{template.header_filename || 'Image'}</span>
                          <span className="text-[9px] text-gray-400">Stored on Meta</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center text-gray-400">
                          <Image className="w-8 h-8 mb-1" /><span className="text-[10px]">Image Header</span>
                        </div>
                      )}
                    </div>
                  )}
                  {template.header_type === 'VIDEO' && (
                    <div className="h-36 bg-gray-800 flex items-center justify-center overflow-hidden relative">
                      {videoBlob ? (
                        <video src={videoBlob} className="w-full h-full object-cover" controls muted playsInline />
                      ) : headerPreview ? (
                        <>
                          <img src={headerPreview} alt="Video thumbnail" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center">
                              <Video className="w-5 h-5 text-gray-800" />
                            </div>
                          </div>
                        </>
                      ) : template.header_content?.startsWith('https://') ? (
                        <video src={template.header_content} className="w-full h-full object-cover" controls muted playsInline />
                      ) : (
                        <div className="flex flex-col items-center text-gray-400 gap-1">
                          <Video className="w-10 h-10" />
                          <span className="text-[10px] font-medium">{template.header_filename || 'Video Header'}</span>
                          {template.header_content && <span className="text-[9px] text-gray-500">Stored on Meta</span>}
                        </div>
                      )}
                    </div>
                  )}
                  {template.header_type === 'DOCUMENT' && (
                    <div className="h-20 bg-gray-100 flex items-center px-3 gap-3">
                      <div className="w-10 h-10 bg-[#EB493D] rounded flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-medium text-gray-700 truncate">{template.header_filename || 'document.pdf'}</p>
                        <p className="text-[10px] text-gray-500">PDF Document</p>
                      </div>
                    </div>
                  )}
                  {template.header_type === 'TEXT' && headerText && (
                    <div className="px-3 py-2">
                      <p className="text-[14px] font-semibold text-[#131517]">{headerText}</p>
                    </div>
                  )}
                  {template.header_type === 'LOCATION' && (
                    <div className="h-32 bg-green-100 flex items-center justify-center">
                      <div className="flex flex-col items-center text-green-600">
                        <div className="w-8 h-8 mb-1 rounded-full bg-green-500 flex items-center justify-center">
                          <span className="text-white text-lg">📍</span>
                        </div>
                        <span className="text-[10px]">Location</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Body */}
              <div className="px-3 py-2">
                <p className="text-[13px] text-[#131517] leading-relaxed">
                  {renderWhatsAppText(bodyText) || <span className="text-gray-400 italic">Message body preview...</span>}
                </p>
              </div>

              {/* Footer */}
              {template.footer_text && (
                <div className="px-3 pb-1">
                  <p className="text-[11px] text-[#8A8A8B]">{template.footer_text}</p>
                </div>
              )}

              {/* Timestamp */}
              <div className="px-3 pb-2 flex justify-end">
                <span className="text-[10px] text-[#8A8A8B]">12:00 PM ✓✓</span>
              </div>

              {/* Buttons */}
              {template.buttons && template.buttons.length > 0 && (
                <div className="border-t border-gray-100">
                  {template.buttons.map((btn, idx) => (
                    <div key={idx} className="border-b border-gray-100 last:border-b-0 px-3 py-2.5 flex items-center justify-center gap-2">
                      {btn.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 text-[#00A884]" />}
                      {btn.type === 'URL' && <ExternalLink className="w-4 h-4 text-[#00A884]" />}
                      {btn.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 text-[#00A884]" />}
                      {btn.type === 'COPY_CODE' && <Copy className="w-4 h-4 text-[#00A884]" />}
                      <span className="text-[13px] text-[#00A884] font-medium">{btn.text || btn.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Input bar */}
          <div className="bg-[#F0F2F5] px-2 py-2 flex items-center gap-2 flex-shrink-0">
            <div className="flex-1 bg-white rounded-full px-3 py-1.5">
              <span className="text-[11px] text-gray-400">Message</span>
            </div>
            <div className="w-7 h-7 bg-[#00A884] rounded-full flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex items-center justify-center h-6 mt-1">
          <div className="w-24 h-1 bg-[#555] rounded-full" />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
//  Formatting Toolbar
// ─────────────────────────────────────────────
const FormattingToolbar = ({ textareaRef, value, onChange, bodyVariables }) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showEmojiPicker]);

  const wrapSelection = useCallback((prefix, suffix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end);
    const newValue = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    onChange(newValue);
    // Restore cursor after state update
    setTimeout(() => {
      ta.focus();
      const newPos = selected.length > 0 ? start + prefix.length + selected.length + suffix.length : start + prefix.length;
      ta.setSelectionRange(newPos, newPos);
    }, 0);
  }, [textareaRef, value, onChange]);

  const insertAtCursor = useCallback((text) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newValue = value.slice(0, start) + text + value.slice(end);
    onChange(newValue);
    setTimeout(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }, [textareaRef, value, onChange]);

  const insertVariable = useCallback(() => {
    const nextVar = `{{${bodyVariables.length + 1}}}`;
    insertAtCursor(nextVar);
  }, [bodyVariables, insertAtCursor]);

  const onEmojiClick = useCallback((emojiData) => {
    insertAtCursor(emojiData.emoji);
    setShowEmojiPicker(false);
  }, [insertAtCursor]);

  const tools = [
    { icon: <Bold className="w-3.5 h-3.5" />, title: 'Bold (*text*)', action: () => wrapSelection('*', '*') },
    { icon: <Italic className="w-3.5 h-3.5" />, title: 'Italic (_text_)', action: () => wrapSelection('_', '_') },
    { icon: <Strikethrough className="w-3.5 h-3.5" />, title: 'Strikethrough (~text~)', action: () => wrapSelection('~', '~') },
    { icon: <Code2 className="w-3.5 h-3.5" />, title: 'Monospace (```text```)', action: () => wrapSelection('```', '```') },
  ];

  return (
    <div className="flex items-center gap-1 p-1.5 bg-[#F7F8FA] border border-[#D9E2E5] rounded-t-md border-b-0 flex-wrap">
      {tools.map((tool, i) => (
        <button
          key={i}
          type="button"
          title={tool.title}
          onClick={tool.action}
          className="flex items-center justify-center w-7 h-7 rounded hover:bg-[#E5E7EB] text-[#595C5C] hover:text-[#131517] transition-colors"
        >
          {tool.icon}
        </button>
      ))}

      <div className="w-px h-4 bg-[#D9E2E5] mx-0.5" />

      {/* Add Variable */}
      <button
        type="button"
        title={`Add variable ({{${bodyVariables.length + 1}}})`}
        onClick={insertVariable}
        className="flex items-center gap-1 h-7 px-2 rounded hover:bg-[#E5E7EB] text-[#595C5C] hover:text-[#131517] transition-colors text-[11px] font-mono"
      >
        <Hash className="w-3 h-3" />
        <span>{`{{${bodyVariables.length + 1}}}`}</span>
      </button>

      <div className="w-px h-4 bg-[#D9E2E5] mx-0.5" />

      {/* Emoji Picker */}
      <div className="relative" ref={pickerRef}>
        <button
          type="button"
          title="Insert emoji"
          onClick={() => setShowEmojiPicker(v => !v)}
          className={`flex items-center justify-center w-7 h-7 rounded transition-colors ${showEmojiPicker ? 'bg-[#E5E7EB] text-[#131517]' : 'hover:bg-[#E5E7EB] text-[#595C5C] hover:text-[#131517]'}`}
        >
          <Smile className="w-3.5 h-3.5" />
        </button>
        {showEmojiPicker && (
          <div className="absolute left-0 top-9 z-50 shadow-xl">
            <EmojiPicker
              onEmojiClick={onEmojiClick}
              searchPlaceholder="Search emojis..."
              height={380}
              width={320}
              previewConfig={{ showPreview: false }}
            />
          </div>
        )}
      </div>

      <div className="ml-auto text-[10px] text-[#AFB0B0] italic pr-1 hidden sm:block">
        Select text then click a format button
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
//  Button Builder
// ─────────────────────────────────────────────
const ButtonBuilder = ({ buttons, onChange, category, buttonErrors = [] }) => {
  const maxQuickReply = 3;
  const maxCallToAction = 2;
  const quickReplyCount = buttons.filter(b => b.type === 'QUICK_REPLY').length;
  const ctaCount = buttons.filter(b => ['PHONE_NUMBER', 'URL'].includes(b.type)).length;

  const addButton = (type) => {
    if (type === 'QUICK_REPLY' && quickReplyCount >= maxQuickReply) return;
    if (['PHONE_NUMBER', 'URL'].includes(type) && ctaCount >= maxCallToAction) return;
    if (buttons.length >= 10) return;
    const newButton = { type, text: '' };
    if (type === 'URL') newButton.url = '';
    if (type === 'PHONE_NUMBER') newButton.phone_number = '';
    if (type === 'COPY_CODE') newButton.example = '';
    onChange([...buttons, newButton]);
  };

  const updateButton = (index, field, value) => {
    const updated = [...buttons];
    updated[index][field] = value;
    onChange(updated);
  };

  const removeButton = (index) => onChange(buttons.filter((_, i) => i !== index));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-[#131517]">Buttons (Optional)</Label>
        <span className="text-[11px] text-[#8A8A8B]">{buttons.length}/10 buttons</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {category !== 'AUTHENTICATION' && (<>
          <Button type="button" variant="outline" size="sm" onClick={() => addButton('QUICK_REPLY')}
            disabled={quickReplyCount >= maxQuickReply || buttons.length >= 10}
            className="border-[#D9E2E5] text-[12px]">
            <MessageSquare className="w-3 h-3 mr-1" />Quick Reply ({quickReplyCount}/{maxQuickReply})
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => addButton('URL')}
            disabled={ctaCount >= maxCallToAction || buttons.length >= 10}
            className="border-[#D9E2E5] text-[12px]">
            <ExternalLink className="w-3 h-3 mr-1" />URL
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => addButton('PHONE_NUMBER')}
            disabled={ctaCount >= maxCallToAction || buttons.length >= 10}
            className="border-[#D9E2E5] text-[12px]">
            <Phone className="w-3 h-3 mr-1" />Phone
          </Button>
        </>)}
        {category === 'AUTHENTICATION' && (
          <Button type="button" variant="outline" size="sm" onClick={() => addButton('COPY_CODE')}
            disabled={buttons.some(b => b.type === 'COPY_CODE') || buttons.length >= 10}
            className="border-[#D9E2E5] text-[12px]">
            <Copy className="w-3 h-3 mr-1" />Copy Code
          </Button>
        )}
      </div>
      {buttons.length > 0 && (
        <div className="space-y-3">
          {buttons.map((btn, idx) => (
            <div key={idx} className="p-3 bg-[#EFEFEF] rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-[#8A8A8B] uppercase">
                  {btn.type === 'QUICK_REPLY' && 'Quick Reply'}
                  {btn.type === 'URL' && 'URL Button'}
                  {btn.type === 'PHONE_NUMBER' && 'Phone Button'}
                  {btn.type === 'COPY_CODE' && 'Copy Code'}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeButton(idx)} className="h-6 w-6 p-0 text-[#EB493D]">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {btn.type === 'COPY_CODE' ? (
                <p className="text-[12px] text-[#8A8A8B]">Button text and code are preset by Meta.</p>
              ) : (() => {
                const err = buttonErrors[idx] || {};
                return (<>
                  <div className="space-y-0.5">
                    <Input placeholder="Button text *" value={btn.text} onChange={(e) => updateButton(idx, 'text', e.target.value)}
                      className={`h-9 text-[13px] ${err.text ? 'border-red-400' : 'border-[#D9E2E5]'}`} maxLength={25} />
                    {err.text && <p className="text-[11px] text-red-500">{err.text}</p>}
                  </div>
                  {btn.type === 'URL' && (
                    <div className="space-y-0.5">
                      <Input placeholder="https://example.com *" value={btn.url || ''}
                        onChange={(e) => updateButton(idx, 'url', e.target.value)}
                        className={`h-9 text-[13px] ${err.url ? 'border-red-400' : 'border-[#D9E2E5]'}`} />
                      {err.url && <p className="text-[11px] text-red-500">{err.url}</p>}
                    </div>
                  )}
                  {btn.type === 'PHONE_NUMBER' && (
                    <div className="space-y-0.5">
                      <Input placeholder="+911234567890 *" value={btn.phone_number || ''}
                        onChange={(e) => updateButton(idx, 'phone_number', e.target.value)}
                        className={`h-9 text-[13px] ${err.phone_number ? 'border-red-400' : 'border-[#D9E2E5]'}`} />
                      {err.phone_number && <p className="text-[11px] text-red-500">{err.phone_number}</p>}
                    </div>
                  )}
                </>);
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
//  Variable extractor
// ─────────────────────────────────────────────
const extractVariables = (text) => {
  if (!text) return [];
  const matches = text.match(/\{\{(\d+)\}\}/g);
  if (!matches) return [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))].sort((a, b) => parseInt(a) - parseInt(b));
};

// ─────────────────────────────────────────────
//  Main TemplateBuilder Component
// ─────────────────────────────────────────────
const TemplateBuilder = ({ initialData, onSave, onCancel, saving, isEditMode = false }) => {
  const bodyTextareaRef = useRef(null);

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    category: initialData?.category || 'UTILITY',
    language: initialData?.language || 'en',
    header_type: initialData?.header_type || '',
    header_content: initialData?.header_content || '',
    header_example: initialData?.header_example || '',
    body_text: initialData?.body_text || '',
    body_examples: initialData?.body_examples || [],
    footer_text: initialData?.footer_text || '',
    buttons: initialData?.buttons || [],
    submit_to_whatsapp: initialData?.submit_to_whatsapp || false,
    header_preview: initialData?.header_preview || '',
  });

  const [sampleValues, setSampleValues] = useState(() => {
    const vals = {};
    // Pre-fill body examples: body_examples[0] → {{1}}, body_examples[1] → {{2}}, etc.
    (initialData?.body_examples || []).forEach((val, i) => { if (val) vals[String(i + 1)] = val; });
    // Pre-fill header variable example
    if (initialData?.header_example) {
      const headerVars = initialData.header_type === 'TEXT'
        ? (initialData.header_content?.match(/\{\{(\d+)\}\}/g) || []).map(m => m.replace(/\{\{|\}\}/g, ''))
        : [];
      if (headerVars.length > 0) vals[headerVars[0]] = initialData.header_example;
    }
    return vals;
  });
  const [activeTab, setActiveTab] = useState('builder');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [videoBlob, setVideoBlob] = useState('');

  const bodyVariables = useMemo(() => extractVariables(formData.body_text), [formData.body_text]);
  const headerVariables = useMemo(() =>
    formData.header_type === 'TEXT' ? extractVariables(formData.header_content) : [],
    [formData.header_type, formData.header_content]
  );
  const allVariables = useMemo(() => [...new Set([...headerVariables, ...bodyVariables])], [headerVariables, bodyVariables]);

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));


  const handleBodyChange = useCallback((newValue) => {
    updateField('body_text', newValue);
  }, []); // eslint-disable-line

  const bodyVarError = formData.category !== 'AUTHENTICATION' &&
    /^\{\{\d+\}\}|\{\{\d+\}\}$/.test(formData.body_text.trim());

  const mediaHeaderError = formData.category !== 'AUTHENTICATION' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(formData.header_type) && !formData.header_content.trim();

  const sampleValuesError = allVariables.length > 0 && allVariables.some(v => !sampleValues[v]?.trim());

  const buttonErrors = formData.buttons.map(btn => {
    const errors = {};
    if (btn.type === 'COPY_CODE') return errors;
    if (!btn.text?.trim()) errors.text = 'Button text is required';
    if (btn.type === 'URL') {
      if (!btn.url?.trim()) errors.url = 'URL is required';
      else if (!/^https?:\/\/.+/.test(btn.url.trim())) errors.url = 'Must start with http:// or https://';
    }
    if (btn.type === 'PHONE_NUMBER') {
      if (!btn.phone_number?.trim()) errors.phone_number = 'Phone number is required';
      else if (!/^\+[1-9]\d{6,14}$/.test(btn.phone_number.trim())) errors.phone_number = 'Use E.164 format, e.g. +911234567890';
    }
    return errors;
  });
  const hasButtonErrors = buttonErrors.some(e => Object.keys(e).length > 0);
  const authMissingButton = formData.category === 'AUTHENTICATION' && !formData.buttons.some(b => b.type === 'COPY_CODE');

  const handleSave = () => {
    if (!formData.body_text) return;
    if (!isEditMode && !formData.name) return;
    if (bodyVarError) return;
    const body_examples = bodyVariables.map(n => sampleValues[n] || `Sample${n}`);
    const header_example = headerVariables.length > 0 ? (sampleValues[headerVariables[0]] || 'Sample') : formData.header_example;
    onSave({ ...formData, body_examples, header_example });
  };

  const languages = [
    { code: 'af', name: 'Afrikaans' },
    { code: 'sq', name: 'Albanian' },
    { code: 'ar', name: 'Arabic' },
    { code: 'ar_EG', name: 'Arabic (EGY)' },
    { code: 'ar_AE', name: 'Arabic (UAE)' },
    { code: 'ar_LB', name: 'Arabic (LBN)' },
    { code: 'ar_MA', name: 'Arabic (MAR)' },
    { code: 'ar_QA', name: 'Arabic (QAT)' },
    { code: 'az', name: 'Azerbaijani' },
    { code: 'be_BY', name: 'Belarusian' },
    { code: 'bn', name: 'Bengali' },
    { code: 'bn_IN', name: 'Bengali (IND)' },
    { code: 'bg', name: 'Bulgarian' },
    { code: 'ca', name: 'Catalan' },
    { code: 'zh_CN', name: 'Chinese (CHN)' },
    { code: 'zh_HK', name: 'Chinese (HKG)' },
    { code: 'zh_TW', name: 'Chinese (TAI)' },
    { code: 'hr', name: 'Croatian' },
    { code: 'cs', name: 'Czech' },
    { code: 'da', name: 'Danish' },
    { code: 'prs_AF', name: 'Dari' },
    { code: 'nl', name: 'Dutch' },
    { code: 'nl_BE', name: 'Dutch (BEL)' },
    { code: 'en', name: 'English' },
    { code: 'en_GB', name: 'English (UK)' },
    { code: 'en_US', name: 'English (US)' },
    { code: 'en_AE', name: 'English (UAE)' },
    { code: 'en_AU', name: 'English (AUS)' },
    { code: 'en_CA', name: 'English (CAN)' },
    { code: 'en_GH', name: 'English (GHA)' },
    { code: 'en_IE', name: 'English (IRL)' },
    { code: 'en_IN', name: 'English (IND)' },
    { code: 'en_JM', name: 'English (JAM)' },
    { code: 'en_MY', name: 'English (MYS)' },
    { code: 'en_NZ', name: 'English (NZL)' },
    { code: 'en_QA', name: 'English (QAT)' },
    { code: 'en_SG', name: 'English (SGP)' },
    { code: 'en_UG', name: 'English (UGA)' },
    { code: 'en_ZA', name: 'English (ZAF)' },
    { code: 'et', name: 'Estonian' },
    { code: 'fil', name: 'Filipino' },
    { code: 'fi', name: 'Finnish' },
    { code: 'fr', name: 'French' },
    { code: 'fr_BE', name: 'French (BEL)' },
    { code: 'fr_CA', name: 'French (CAN)' },
    { code: 'fr_CH', name: 'French (CHE)' },
    { code: 'fr_CI', name: 'French (CIV)' },
    { code: 'fr_MA', name: 'French (MAR)' },
    { code: 'ka', name: 'Georgian' },
    { code: 'de', name: 'German' },
    { code: 'de_AT', name: 'German (AUT)' },
    { code: 'de_CH', name: 'German (CHE)' },
    { code: 'el', name: 'Greek' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'ha', name: 'Hausa' },
    { code: 'he', name: 'Hebrew' },
    { code: 'hi', name: 'Hindi' },
    { code: 'hu', name: 'Hungarian' },
    { code: 'id', name: 'Indonesian' },
    { code: 'ga', name: 'Irish' },
    { code: 'it', name: 'Italian' },
    { code: 'ja', name: 'Japanese' },
    { code: 'kn', name: 'Kannada' },
    { code: 'kk', name: 'Kazakh' },
    { code: 'rw_RW', name: 'Kinyarwanda' },
    { code: 'ko', name: 'Korean' },
    { code: 'ky_KG', name: 'Kyrgyz (Kyrgyzstan)' },
    { code: 'lo', name: 'Lao' },
    { code: 'lv', name: 'Latvian' },
    { code: 'lt', name: 'Lithuanian' },
    { code: 'mk', name: 'Macedonian' },
    { code: 'ms', name: 'Malay' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'mr', name: 'Marathi' },
    { code: 'nb', name: 'Norwegian' },
    { code: 'ps_AF', name: 'Pashto' },
    { code: 'fa', name: 'Persian' },
    { code: 'pl', name: 'Polish' },
    { code: 'pt_BR', name: 'Portuguese (BR)' },
    { code: 'pt_PT', name: 'Portuguese (POR)' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'ro', name: 'Romanian' },
    { code: 'ru', name: 'Russian' },
    { code: 'sr', name: 'Serbian' },
    { code: 'si_LK', name: 'Sinhala' },
    { code: 'sk', name: 'Slovak' },
    { code: 'sl', name: 'Slovenian' },
    { code: 'es', name: 'Spanish' },
    { code: 'es_AR', name: 'Spanish (ARG)' },
    { code: 'es_CL', name: 'Spanish (CHL)' },
    { code: 'es_CO', name: 'Spanish (COL)' },
    { code: 'es_CR', name: 'Spanish (CRI)' },
    { code: 'es_DO', name: 'Spanish (DOM)' },
    { code: 'es_EC', name: 'Spanish (ECU)' },
    { code: 'es_HN', name: 'Spanish (HND)' },
    { code: 'es_MX', name: 'Spanish (MEX)' },
    { code: 'es_PA', name: 'Spanish (PAN)' },
    { code: 'es_PE', name: 'Spanish (PER)' },
    { code: 'es_ES', name: 'Spanish (SPA)' },
    { code: 'es_UY', name: 'Spanish (URY)' },
    { code: 'sw', name: 'Swahili' },
    { code: 'sv', name: 'Swedish' },
    { code: 'ta', name: 'Tamil' },
    { code: 'te', name: 'Telugu' },
    { code: 'th', name: 'Thai' },
    { code: 'tr', name: 'Turkish' },
    { code: 'uk', name: 'Ukrainian' },
    { code: 'ur', name: 'Urdu' },
    { code: 'uz', name: 'Uzbek' },
    { code: 'vi', name: 'Vietnamese' },
    { code: 'zu', name: 'Zulu' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Builder Section */}
      <div className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 bg-[#EFEFEF]">
            <TabsTrigger value="builder" className="data-[state=active]:bg-white">Builder</TabsTrigger>
            <TabsTrigger value="variables" className="data-[state=active]:bg-white">Variables</TabsTrigger>
            <TabsTrigger value="buttons" className="data-[state=active]:bg-white">Buttons</TabsTrigger>
          </TabsList>

          <TabsContent value="builder" className="space-y-4 mt-4">
            {/* Basic Info — locked in edit mode */}
            {!isEditMode ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#131517]">Template Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''))}
                    placeholder="order_confirmation"
                    className="border-[#D9E2E5]"
                    data-testid="template-name-input"
                  />
                  <p className="text-[10px] text-[#8A8A8B]">Lowercase, underscores only</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#131517]">Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => {
                    if (v === 'AUTHENTICATION') {
                      setFormData(prev => ({
                        ...prev,
                        category: v,
                        header_type: '',
                        header_content: '',
                        footer_text: '',
                        body_text: '{{1}} is your verification code. For your security, do not share this code.',
                        buttons: [{ type: 'COPY_CODE', text: 'Copy Code' }],
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        category: v,
                        body_text: prev.category === 'AUTHENTICATION' ? '' : prev.body_text,
                        buttons: prev.buttons.filter(b => b.type !== 'COPY_CODE'),
                      }));
                    }
                  }}>
                    <SelectTrigger className="border-[#D9E2E5]" data-testid="template-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTILITY">Utility (Transactional)</SelectItem>
                      <SelectItem value="MARKETING">Marketing (Promotional)</SelectItem>
                      <SelectItem value="AUTHENTICATION">Authentication (OTP)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              /* Edit mode: show locked name/category/language */
              <div className="p-3 bg-[#F7F8FA] border border-[#D9E2E5] rounded-lg space-y-2">
                <p className="text-[11px] font-semibold text-[#8A8A8B] uppercase flex items-center gap-1">
                  <Info className="w-3.5 h-3.5" /> Locked fields (per Meta rules)
                </p>
                <div className="grid grid-cols-3 gap-3 text-[12px]">
                  <div><span className="text-[#8A8A8B]">Name: </span><span className="font-medium text-[#131517]">{formData.name}</span></div>
                  <div><span className="text-[#8A8A8B]">Category: </span><span className="font-medium text-[#131517]">{formData.category}</span></div>
                  <div><span className="text-[#8A8A8B]">Language: </span><span className="font-medium text-[#131517]">{formData.language}</span></div>
                </div>
              </div>
            )}

            {!isEditMode && (
              <div className="space-y-2">
                <Label className="text-[#131517]">Language *</Label>
                <Select value={formData.language} onValueChange={(v) => updateField('language', v)}>
                  <SelectTrigger className="border-[#D9E2E5]"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {languages.map(lang => <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Separator className="bg-[#D9E2E5]" />

            {/* Authentication info banner */}
            {formData.category === 'AUTHENTICATION' && (
              <div className="p-3 bg-[#EEF6FF] border border-blue-200 rounded-lg flex items-start gap-2">
                <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-[12px] text-blue-800 space-y-0.5">
                  <p className="font-semibold">Authentication Template Rules</p>
                  <ul className="list-disc list-inside space-y-0.5 text-[11px] text-blue-700">
                    <li>No header or footer allowed</li>
                    <li>Body text is preset by Meta and cannot be changed</li>
                    <li>Requires a Copy Code button (auto-added)</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Header */}
            {formData.category !== 'AUTHENTICATION' && <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[#131517]">Header (Optional)</Label>
                <span className="text-[10px] text-[#8A8A8B]">Max 60 characters for text</span>
              </div>
              <Select
                value={formData.header_type || 'none'}
                onValueChange={(v) => { updateField('header_type', v === 'none' ? '' : v); if (v === 'none' || v !== formData.header_type) { updateField('header_content', ''); updateField('header_preview', ''); setVideoBlob(''); } }}
              >
                <SelectTrigger className="border-[#D9E2E5]"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="TEXT">Text</SelectItem>
                  <SelectItem value="IMAGE">Image</SelectItem>
                  <SelectItem value="VIDEO">Video</SelectItem>
                  <SelectItem value="DOCUMENT">Document</SelectItem>
                  <SelectItem value="LOCATION">Location</SelectItem>
                </SelectContent>
              </Select>
              {formData.header_type === 'TEXT' && (
                <div className="space-y-1">
                  <Input value={formData.header_content} onChange={(e) => updateField('header_content', e.target.value)}
                    placeholder="Header text with {{1}} variable" className="border-[#D9E2E5]" maxLength={60} />
                  <p className="text-[10px] text-[#8A8A8B]">{formData.header_content.length}/60 characters</p>
                </div>
              )}
              {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(formData.header_type) && (() => {
                const meta = {
                  IMAGE: { icon: Image, accept: 'image/jpeg,image/png', hint: 'JPG or PNG', label: 'image' },
                  VIDEO: { icon: Video, accept: 'video/mp4', hint: 'MP4', label: 'video' },
                  DOCUMENT: { icon: FileText, accept: 'application/pdf', hint: 'PDF', label: 'document' },
                };
                const { icon: Icon, accept, hint, label } = meta[formData.header_type];
                const hasFile = !!formData.header_content?.trim();
                return (
                  <div className="space-y-2">
                    <label
                      className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl transition-colors
                        ${uploadingMedia ? 'cursor-wait opacity-60' : 'cursor-pointer'}
                        ${mediaHeaderError ? 'border-red-400 bg-red-50' : hasFile ? 'border-[#00A884] bg-green-50' : 'border-[#D9E2E5] bg-[#F7F8FA] hover:bg-[#EFEFEF]'}`}
                      style={{ minHeight: 110 }}
                    >
                      <input
                        type="file"
                        accept={accept}
                        className="hidden"
                        disabled={uploadingMedia}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const maxBytes = { IMAGE: 5, VIDEO: 16, DOCUMENT: 100 }[formData.header_type] * 1024 * 1024;
                          if (file.size > maxBytes) {
                            toast.error(`${label.charAt(0).toUpperCase() + label.slice(1)} size must not exceed ${maxBytes / (1024 * 1024)}MB`);
                            e.target.value = '';
                            return;
                          }
                          setUploadingMedia(true);

                          // Generate persistent base64 thumbnail for preview
                          const generatePreview = () => new Promise((resolve) => {
                            if (formData.header_type === 'IMAGE') {
                              const img = new window.Image();
                              const objUrl = URL.createObjectURL(file);
                              img.onload = () => {
                                const canvas = document.createElement('canvas');
                                const MAX = 400;
                                const ratio = Math.min(MAX / img.width, MAX / img.height, 1);
                                canvas.width = img.width * ratio;
                                canvas.height = img.height * ratio;
                                canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                                URL.revokeObjectURL(objUrl);
                                resolve(canvas.toDataURL('image/jpeg', 0.7));
                              };
                              img.onerror = () => { URL.revokeObjectURL(objUrl); resolve(''); };
                              img.src = objUrl;
                            } else if (formData.header_type === 'VIDEO') {
                              const video = document.createElement('video');
                              const objUrl = URL.createObjectURL(file);
                              video.onloadeddata = () => {
                                video.currentTime = 0.5;
                              };
                              video.onseeked = () => {
                                const canvas = document.createElement('canvas');
                                canvas.width = video.videoWidth || 320;
                                canvas.height = video.videoHeight || 180;
                                canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
                                URL.revokeObjectURL(objUrl);
                                resolve(canvas.toDataURL('image/jpeg', 0.7));
                              };
                              video.onerror = () => { URL.revokeObjectURL(objUrl); resolve(''); };
                              video.src = objUrl;
                              video.load();
                            } else {
                              resolve('');
                            }
                          });

                          const preview = await generatePreview();
                          updateField('header_preview', preview);
                          if (formData.header_type === 'VIDEO') {
                            setVideoBlob(URL.createObjectURL(file));
                          }

                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            const token = localStorage.getItem('token');
                            const res = await axios.post(`${API_URL}/templates/upload-media`, fd, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            updateField('header_content', res.data.media_id);
                            updateField('header_filename', file.name);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Media upload failed');
                            updateField('header_preview', '');
                            setVideoBlob('');
                          } finally {
                            setUploadingMedia(false);
                          }
                        }}
                      />
                      {uploadingMedia ? (
                        <div className="py-5 space-y-2 text-center px-4">
                          <div className="w-10 h-10 mx-auto bg-[#EFEFEF] rounded-full flex items-center justify-center animate-spin">
                            <Upload className="w-5 h-5 text-[#8A8A8B]" />
                          </div>
                          <p className="text-[13px] text-[#8A8A8B]">Uploading to Meta...</p>
                        </div>
                      ) : hasFile ? (
                        <div className="flex items-center gap-3 w-full px-4 py-3">
                          {(() => {
                            const isCdnUrl = formData.header_content?.startsWith('https://');
                            if (formData.header_type === 'IMAGE' && (formData.header_preview || isCdnUrl)) {
                              return (
                                <div className="w-12 h-12 flex-shrink-0">
                                  <img src={formData.header_preview || formData.header_content} alt="preview" className="w-12 h-12 object-cover rounded-lg" />
                                </div>
                              );
                            }
                            if (formData.header_type === 'VIDEO') {
                              const imgSrc = formData.header_preview; // base64 thumbnail only (CDN video URL can't be img src)
                              return (
                                <div className="relative w-12 h-12 flex-shrink-0">
                                  {imgSrc
                                    ? <img src={imgSrc} alt="preview" className="w-12 h-12 object-cover rounded-lg" />
                                    : <div className="w-12 h-12 bg-gray-800 rounded-lg" />}
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                                    <Video className="w-4 h-4 text-white" />
                                  </div>
                                </div>
                              );
                            }
                            if (formData.header_type === 'DOCUMENT') {
                              return (
                                <div className="w-12 h-12 bg-[#EB493D] rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Icon className="w-6 h-6 text-white" />
                                </div>
                              );
                            }
                            return (
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#075E54]`}>
                                <Icon className="w-6 h-6 text-white" />
                              </div>
                            );
                          })()}
                          <div className="flex-1 min-w-0 text-left">
                            <p className="text-[12px] font-medium text-[#131517] truncate">{formData.header_filename || 'Uploaded file'}</p>
                            <p className="text-[11px] text-[#00A884] mt-0.5">Uploaded to Meta ✓ — click to change</p>
                          </div>
                          <button type="button"
                            onClick={(e) => { e.preventDefault(); updateField('header_content', ''); updateField('header_filename', ''); updateField('header_preview', ''); setVideoBlob(''); }}
                            className="text-[#EB493D] flex-shrink-0 p-1">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="py-5 space-y-2 text-center px-4">
                          <div className="w-10 h-10 mx-auto bg-[#EFEFEF] rounded-full flex items-center justify-center">
                            <Upload className="w-5 h-5 text-[#8A8A8B]" />
                          </div>
                          <p className="text-[13px] font-medium text-[#131517]">Click to upload {label}</p>
                          <p className="text-[11px] text-[#8A8A8B]">{hint}</p>
                        </div>
                      )}
                    </label>
                    {mediaHeaderError && <p className="text-[11px] text-red-500">{label.charAt(0).toUpperCase() + label.slice(1)} file is required.</p>}
                  </div>
                );
              })()}
              {formData.header_type === 'LOCATION' && (
                <div className="border-2 border-dashed border-[#D9E2E5] rounded-xl p-5 bg-[#F7F8FA] text-center space-y-2">
                  <div className="w-12 h-12 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">📍</span>
                  </div>
                  <p className="text-[13px] font-medium text-[#131517]">Location Header</p>
                  <p className="text-[11px] text-[#8A8A8B]">A location pin will be shown. Coordinates are sent at message time.</p>
                </div>
              )}
            </div>}

            <Separator className="bg-[#D9E2E5]" />

            {/* Body + Formatting Toolbar */}
            <div className="space-y-1">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[#131517]">Body *</Label>
                {formData.category !== 'AUTHENTICATION' && (
                  <span className="text-[10px] text-[#8A8A8B]">Max 1024 characters</span>
                )}
              </div>

              {formData.category === 'AUTHENTICATION' ? (
                <div className="space-y-2">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-[12px] text-amber-700">
                      Meta pre-sets the body for Authentication templates. This cannot be changed.
                    </p>
                  </div>
                  <div className="p-3 bg-[#F7F8FA] border border-[#D9E2E5] rounded-lg text-[13px] text-[#595C5C]">
                    {formData.body_text}
                  </div>
                </div>
              ) : (
                <>
                  <FormattingToolbar
                    textareaRef={bodyTextareaRef}
                    value={formData.body_text}
                    onChange={handleBodyChange}
                    bodyVariables={bodyVariables}
                  />
                  <Textarea
                    ref={bodyTextareaRef}
                    value={formData.body_text}
                    onChange={(e) => updateField('body_text', e.target.value)}
                    placeholder={`Hello {{1}},\n\nYour order {{2}} has been confirmed.\n\nThank you for shopping with us!`}
                    rows={6}
                    className={`text-[13px] rounded-t-none border-t-0 ${bodyVarError ? 'border-red-500' : 'border-[#D9E2E5]'}`}
                    maxLength={1024}
                    data-testid="template-body-input"
                  />
                  <div className="flex items-center justify-between">
                    {bodyVarError
                      ? <p className="text-[11px] text-red-500">Variables can&apos;t be at the start or end of the template.</p>
                      : <p className="text-[10px] text-[#8A8A8B]">Use {'{{1}}'}, {'{{2}}'}, etc. for dynamic variables</p>
                    }
                    <p className="text-[10px] text-[#8A8A8B]">{formData.body_text.length}/1024</p>
                  </div>
                </>
              )}
            </div>

            <Separator className="bg-[#D9E2E5]" />

            {/* Footer */}
            {formData.category !== 'AUTHENTICATION' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[#131517]">Footer (Optional)</Label>
                  <span className="text-[10px] text-[#8A8A8B]">Max 60 characters, no variables</span>
                </div>
                <Input value={formData.footer_text} onChange={(e) => updateField('footer_text', e.target.value)}
                  placeholder="Reply STOP to unsubscribe" className="border-[#D9E2E5]" maxLength={60} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="variables" className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-[12px] text-blue-700">
                  <p className="font-medium mb-1">Variable Examples</p>
                  <p>For template approval, provide example values for each variable. These help Meta understand the context of your template.</p>
                </div>
              </div>
            </div>
            {allVariables.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-[#131517]">Sample Values for Preview <span className="text-red-500">*</span></Label>
                  <span className="text-[11px] text-[#8A8A8B]">Required by Meta</span>
                </div>
                {allVariables.map((varNum) => {
                  const isEmpty = !sampleValues[varNum]?.trim();
                  return (
                    <div key={varNum} className="space-y-0.5">
                      <div className="flex items-center gap-3">
                        <span className="text-[13px] font-medium text-[#595C5C] w-16 flex-shrink-0">{`{{${varNum}}}`}</span>
                        <Input value={sampleValues[varNum] || ''} onChange={(e) => setSampleValues({ ...sampleValues, [varNum]: e.target.value })}
                          placeholder={`Sample value for {{${varNum}}} *`}
                          className={`flex-1 ${isEmpty ? 'border-red-400' : 'border-[#D9E2E5]'}`} />
                      </div>
                      {isEmpty && <p className="text-[11px] text-red-500 ml-[76px]">Sample value is required</p>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 mx-auto text-[#D9E2E5] mb-2" />
                <p className="text-[13px] text-[#8A8A8B]">No variables detected</p>
                <p className="text-[11px] text-[#AFB0B0] mt-1">Add {'{{1}}'}, {'{{2}}'} in body or header text</p>
              </div>
            )}
            <Separator className="bg-[#D9E2E5]" />
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Variable Tips</Label>
              <ul className="text-[12px] text-[#595C5C] space-y-1 list-disc list-inside">
                <li>Variables must be numbered sequentially: {'{{1}}'}, {'{{2}}'}, {'{{3}}'}</li>
                <li>Authentication templates: max 15 characters per variable</li>
                <li>Don't use special characters like #, $, % in examples</li>
                <li>Variables in URLs: {'https://example.com/order/{{1}}'}</li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="buttons" className="mt-4">
            <ButtonBuilder buttons={formData.buttons} onChange={(btns) => updateField('buttons', btns)} category={formData.category} buttonErrors={buttonErrors} />
            <Separator className="bg-[#D9E2E5] my-4" />
            <div className="space-y-2">
              <Label className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Button Guidelines</Label>
              <ul className="text-[12px] text-[#595C5C] space-y-1 list-disc list-inside">
                <li>Maximum 10 buttons total per template</li>
                <li>Up to 3 Quick Reply buttons</li>
                <li>Up to 2 Call-to-Action buttons (URL + Phone combined)</li>
                <li>Button text: max 25 characters</li>
                <li>Copy Code: Only for Authentication templates</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-[#D9E2E5]">
          {!isEditMode ? (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="submitToWhatsApp" checked={formData.submit_to_whatsapp}
                onChange={(e) => updateField('submit_to_whatsapp', e.target.checked)} className="rounded border-[#D9E2E5]" />
              <Label htmlFor="submitToWhatsApp" className="text-[12px] text-[#595C5C] cursor-pointer">Submit for approval</Label>
            </div>
          ) : (
            <div className="text-[11px] text-[#8A8A8B]">Changes will be submitted to Meta for review</div>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel} className="border-[#D9E2E5]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || uploadingMedia || !formData.body_text || (!isEditMode && !formData.name) || bodyVarError || mediaHeaderError || sampleValuesError || hasButtonErrors || authMissingButton}
              className="bg-[#131517] hover:bg-[#333537]" data-testid="save-template-btn">
              {saving ? <span className="animate-spin mr-2">⏳</span> : <CheckCircle className="w-4 h-4 mr-2" />}
              {isEditMode ? 'Save Changes' : 'Save Template'}
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-[#131517] text-[16px] font-semibold">Live Preview</Label>
          <span className="text-[11px] text-[#8A8A8B]">Updates as you type</span>
        </div>
        <WhatsAppPreview template={formData} sampleValues={sampleValues} headerPreview={formData.header_preview} videoBlob={videoBlob} />

        <Card className="border-[#D9E2E5]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Template Info</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <div><span className="text-[#8A8A8B]">Category:</span><span className="ml-2 text-[#131517]">{formData.category}</span></div>
              <div><span className="text-[#8A8A8B]">Language:</span><span className="ml-2 text-[#131517]">{formData.language}</span></div>
              <div><span className="text-[#8A8A8B]">Variables:</span><span className="ml-2 text-[#131517]">{allVariables.length}</span></div>
              <div><span className="text-[#8A8A8B]">Buttons:</span><span className="ml-2 text-[#131517]">{formData.buttons.length}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TemplateBuilder;
